/* =========================================================
   BOX IMPORT — Minijuego de reparto
   Archivo: assets/box-import-game.js
   Aparece cuando el cliente se queda sin conexión o cuando
   una página tarda en cargar. Todo se dibuja en un <canvas>:
   no usa imágenes, así funciona aunque no haya internet.
   ========================================================= */
(() => {
  if (customElements.get('bi-delivery-game')) return;

  const BEST_KEY = 'bi-game-best';
  const W = 360;
  const H = 520;
  const LANES = 3;
  const LANE_W = W / LANES;
  const NAVY = '#1F2B40';
  const MUSTARD = '#F5CF52';
  const CAR_COLORS = ['#E4572E', '#3FA7D6', '#59CD90', '#F4F1EA', '#9B5DE5', '#FF8C42'];

  const laneX = (lane) => lane * LANE_W + LANE_W / 2;
  const rand = (min, max) => min + Math.random() * (max - min);

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  /* ---------- Motor del juego ---------- */
  class DeliveryRun {
    constructor(canvas, { onScore, onGameOver, bestKey = BEST_KEY } = {}) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.onScore = onScore;
      this.onGameOver = onGameOver;
      this.bestKey = bestKey;
      this.best = 0;
      try { this.best = Number(localStorage.getItem(this.bestKey)) || 0; } catch (e) { /* sin storage */ }
      this.resize();
      this.reset();
      this.state = 'ready';
      this.draw();
    }

    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = W * dpr;
      this.canvas.height = H * dpr;
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    reset() {
      this.player = { lane: 1, x: laneX(1), y: H - 120, w: 50, h: 92 };
      this.items = [];
      this.sparks = [];
      this.popups = [];
      this.speed = 250;
      this.score = 0;
      this.road = 0;
      this.spawnIn = 0.6;
      this.time = 0;
      this.shake = 0;
    }

    start() {
      this.reset();
      this.state = 'playing';
      this.onScore?.(0, this.best);
      this.resume();
    }

    resume() {
      if (this.raf || this.state !== 'playing') return;
      this.last = performance.now();
      const loop = (t) => {
        const dt = Math.min((t - this.last) / 1000, 0.05);
        this.last = t;
        this.update(dt);
        this.draw();
        this.raf = this.state === 'playing' || this.shake > 0 ? requestAnimationFrame(loop) : 0;
      };
      this.raf = requestAnimationFrame(loop);
    }

    pause() {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    move(dir) {
      if (this.state !== 'playing') return;
      this.player.lane = Math.max(0, Math.min(LANES - 1, this.player.lane + dir));
    }

    spawn() {
      // Nunca bloqueamos los tres carriles: siempre queda un camino libre
      const blocked = new Set(this.items.filter((i) => i.type === 'car' && i.y < 170).map((i) => i.lane));
      const lane = Math.floor(Math.random() * LANES);
      let type = Math.random() < 0.34 ? 'pkg' : 'car';
      if (type === 'car' && (blocked.size >= LANES - 1 && !blocked.has(lane))) type = 'pkg';
      if (type === 'car') {
        this.items.push({
          type, lane, x: laneX(lane), y: -110, w: 46, h: 82,
          color: CAR_COLORS[Math.floor(Math.random() * CAR_COLORS.length)],
          drift: rand(-0.15, 0.15),
        });
      } else {
        this.items.push({ type, lane, x: laneX(lane), y: -60, w: 34, h: 34, spin: rand(0, Math.PI) });
      }
    }

    update(dt) {
      if (this.shake > 0) this.shake = Math.max(0, this.shake - dt);
      if (this.state !== 'playing') return;

      this.time += dt;
      this.speed = Math.min(640, 250 + this.time * 11);
      this.road = (this.road + this.speed * dt) % 60;

      // El camión se desliza suave hacia su carril
      const p = this.player;
      p.x += (laneX(p.lane) - p.x) * Math.min(1, dt * 14);

      this.spawnIn -= dt;
      if (this.spawnIn <= 0) {
        this.spawn();
        this.spawnIn = rand(0.45, 0.85) * (250 / this.speed) + 0.18;
      }

      const hitbox = { x: p.x - p.w / 2 + 6, y: p.y + 6, w: p.w - 12, h: p.h - 12 };
      for (const it of this.items) {
        it.y += this.speed * dt * (it.type === 'car' ? 0.75 : 1);
        if (it.type === 'pkg') it.spin += dt * 3;
        const box = { x: it.x - it.w / 2, y: it.y, w: it.w, h: it.h };
        const hit = box.x < hitbox.x + hitbox.w && box.x + box.w > hitbox.x && box.y < hitbox.y + hitbox.h && box.y + box.h > hitbox.y;
        if (!hit || it.dead) continue;
        if (it.type === 'pkg') {
          it.dead = true;
          this.score++;
          this.onScore?.(this.score, this.best);
          this.popups.push({ x: it.x, y: it.y, life: 0.8 });
          for (let i = 0; i < 8; i++) {
            this.sparks.push({ x: it.x, y: it.y + 10, vx: rand(-120, 120), vy: rand(-160, -40), life: 0.5 });
          }
        } else {
          this.crash();
          return;
        }
      }
      this.items = this.items.filter((i) => !i.dead && i.y < H + 120);
      this.sparks = this.sparks.filter((s) => (s.life -= dt) > 0);
      this.sparks.forEach((s) => { s.x += s.vx * dt; s.y += s.vy * dt; s.vy += 400 * dt; });
      this.popups = this.popups.filter((s) => (s.life -= dt) > 0);
    }

    crash() {
      this.state = 'over';
      this.shake = 0.35;
      if (this.score > this.best) {
        this.best = this.score;
        try { localStorage.setItem(this.bestKey, String(this.best)); } catch (e) { /* sin storage */ }
      }
      this.onGameOver?.(this.score, this.best);
    }

    /* ---------- Dibujo ---------- */
    draw() {
      const c = this.ctx;
      c.save();
      if (this.shake > 0) c.translate(rand(-6, 6) * this.shake * 3, rand(-6, 6) * this.shake * 3);

      // Ruta
      c.fillStyle = '#2A3954';
      c.fillRect(-10, -10, W + 20, H + 20);
      c.fillStyle = MUSTARD;
      c.fillRect(0, 0, 6, H);
      c.fillRect(W - 6, 0, 6, H);
      c.fillStyle = 'rgba(255, 255, 255, .35)';
      for (let l = 1; l < LANES; l++) {
        for (let y = -60 + this.road; y < H; y += 60) c.fillRect(l * LANE_W - 2, y, 4, 30);
      }

      for (const it of this.items) (it.type === 'car' ? this.drawCar : this.drawPackage).call(this, it);
      this.drawTruck(this.player);

      // Brillitos al agarrar paquetes
      for (const s of this.sparks) {
        c.globalAlpha = Math.max(0, s.life * 2);
        c.fillStyle = MUSTARD;
        c.fillRect(s.x - 3, s.y - 3, 6, 6);
      }
      c.globalAlpha = 1;
      c.font = '700 20px Manrope, system-ui, sans-serif';
      c.textAlign = 'center';
      for (const s of this.popups) {
        c.globalAlpha = Math.min(1, s.life * 2);
        c.fillStyle = MUSTARD;
        c.fillText('+1', s.x, s.y - (0.8 - s.life) * 50);
      }
      c.globalAlpha = 1;

      // Contador
      this.drawPill(12, 12, `📦 ${this.score}`, 'left');
      this.drawPill(W - 12, 12, `Récord ${Math.max(this.best, this.score)}`, 'right');
      c.restore();
    }

    drawPill(x, y, text, align) {
      const c = this.ctx;
      c.font = '800 15px Manrope, system-ui, sans-serif';
      const w = c.measureText(text).width + 24;
      const left = align === 'left' ? x : x - w;
      c.fillStyle = 'rgba(255, 255, 255, .95)';
      roundRect(c, left, y, w, 32, 16);
      c.fill();
      c.fillStyle = NAVY;
      c.textAlign = 'left';
      c.textBaseline = 'middle';
      c.fillText(text, left + 12, y + 17);
      c.textBaseline = 'alphabetic';
    }

    drawTruck(p) {
      const c = this.ctx;
      const x = p.x - p.w / 2;
      const y = p.y;
      c.fillStyle = 'rgba(0, 0, 0, .25)';
      roundRect(c, x + 4, y + 6, p.w, p.h, 10);
      c.fill();
      // Cabina (adelante)
      c.fillStyle = NAVY;
      roundRect(c, x + 4, y, p.w - 8, 28, 9);
      c.fill();
      c.fillStyle = '#BFD7F2';
      roundRect(c, x + 9, y + 5, p.w - 18, 11, 4);
      c.fill();
      // Caja de carga
      c.fillStyle = '#FFF8E4';
      roundRect(c, x, y + 24, p.w, p.h - 24, 8);
      c.fill();
      c.strokeStyle = NAVY;
      c.lineWidth = 3;
      c.stroke();
      c.fillStyle = MUSTARD;
      c.fillRect(x + p.w / 2 - 5, y + 26, 10, p.h - 28);
      c.save();
      c.translate(p.x, y + 24 + (p.h - 24) / 2);
      c.rotate(-Math.PI / 2);
      c.fillStyle = NAVY;
      c.font = '400 15px "Bebas Neue", Impact, sans-serif';
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillText('BOX IMPORT', 0, 1);
      c.restore();
    }

    drawCar(car) {
      const c = this.ctx;
      const x = car.x - car.w / 2;
      const y = car.y;
      c.fillStyle = 'rgba(0, 0, 0, .25)';
      roundRect(c, x + 3, y + 5, car.w, car.h, 12);
      c.fill();
      c.fillStyle = car.color;
      roundRect(c, x, y, car.w, car.h, 12);
      c.fill();
      // Vienen de frente: el parabrisas mira hacia abajo
      c.fillStyle = 'rgba(20, 28, 42, .75)';
      roundRect(c, x + 6, y + car.h - 30, car.w - 12, 14, 4);
      c.fill();
      roundRect(c, x + 7, y + 12, car.w - 14, 11, 4);
      c.fill();
      c.fillStyle = '#FFF4B8';
      c.fillRect(x + 4, y + car.h - 5, 9, 4);
      c.fillRect(x + car.w - 13, y + car.h - 5, 9, 4);
    }

    drawPackage(pkg) {
      const c = this.ctx;
      const bob = Math.sin(pkg.spin * 2) * 3;
      c.save();
      c.translate(pkg.x, pkg.y + pkg.h / 2 + bob);
      c.rotate(Math.sin(pkg.spin) * 0.25);
      c.fillStyle = 'rgba(245, 207, 82, .25)';
      c.beginPath();
      c.arc(0, 0, 26, 0, Math.PI * 2);
      c.fill();
      c.fillStyle = '#C8915A';
      roundRect(c, -17, -17, 34, 34, 4);
      c.fill();
      c.fillStyle = MUSTARD;
      c.fillRect(-4, -17, 8, 34);
      c.fillRect(-17, -4, 34, 8);
      c.restore();
    }
  }

  // Lo exponemos para reutilizarlo en la página "Tu envío"
  window.BiDeliveryRun = DeliveryRun;

  /* ---------- Pantalla (sin conexión / carga lenta) ---------- */
  class BiDeliveryGame extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const opts = { signal: this.ac.signal };
      this.delay = (Number(this.dataset.delay) || 1.5) * 1000;
      this.titleEl = this.querySelector('[data-game-title]');
      this.subEl = this.querySelector('[data-game-sub]');
      this.layerStart = this.querySelector('[data-game-start]');
      this.layerOver = this.querySelector('[data-game-over]');
      this.onlineEl = this.querySelector('[data-game-online]');
      this.screen = this.querySelector('.bi-game__screen');

      this.game = new DeliveryRun(this.querySelector('canvas'), {
        onGameOver: (score, best) => {
          this.querySelector('[data-final-score]').textContent = score;
          this.querySelector('[data-final-best]').textContent = best;
          this.layerOver.hidden = false;
        },
      });

      const play = () => {
        this.layerStart.hidden = true;
        this.layerOver.hidden = true;
        this.game.start();
      };
      this.querySelectorAll('[data-game-play]').forEach((b) => b.addEventListener('click', play, opts));
      this.querySelector('[data-game-left]')?.addEventListener('pointerdown', (e) => { e.preventDefault(); this.game.move(-1); }, opts);
      this.querySelector('[data-game-right]')?.addEventListener('pointerdown', (e) => { e.preventDefault(); this.game.move(1); }, opts);
      this.querySelector('[data-game-close]')?.addEventListener('click', () => this.close(), opts);
      this.querySelector('[data-game-back]')?.addEventListener('click', () => {
        if (this.pendingUrl) window.location.href = this.pendingUrl;
        else this.close();
      }, opts);

      // Toque / deslizamiento sobre la ruta
      let startX = null;
      this.screen.addEventListener('pointerdown', (e) => {
        if (e.target.closest('button')) return;
        startX = e.clientX;
      }, opts);
      this.screen.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        const r = this.screen.getBoundingClientRect();
        if (Math.abs(dx) > 30) this.game.move(dx > 0 ? 1 : -1);
        else this.game.move(e.clientX - r.left < r.width / 2 ? -1 : 1);
        startX = null;
      }, opts);

      document.addEventListener('keydown', (e) => {
        if (!this.classList.contains('is-open')) return;
        if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') { this.game.move(-1); e.preventDefault(); }
        if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') { this.game.move(1); e.preventDefault(); }
        if ((e.key === ' ' || e.key === 'Enter') && this.game.state !== 'playing') { play(); e.preventDefault(); }
        if (e.key === 'Escape') this.close();
      }, opts);

      // Sin conexión
      window.addEventListener('offline', () => this.open('offline'), opts);
      window.addEventListener('online', () => {
        if (this.mode === 'offline' && this.classList.contains('is-open')) this.onlineEl.hidden = false;
      }, opts);

      // Cargas lentas: si la página nueva tarda, mostramos el juego
      document.addEventListener('click', (e) => this.onLinkClick(e), opts);
      document.addEventListener('submit', (e) => this.onSubmit(e), opts);
      window.addEventListener('pageshow', () => { clearTimeout(this.loadTimer); if (this.mode === 'loading') this.close(); }, opts);
      window.addEventListener('pagehide', () => clearTimeout(this.loadTimer), opts);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.game.pause();
        else if (this.classList.contains('is-open')) this.game.resume();
      }, opts);

      // Editor de Shopify: se muestra al seleccionar la sección
      if (this.dataset.designMode === 'true') {
        document.addEventListener('shopify:section:select', (e) => {
          if (this.closest('.shopify-section')?.id === `shopify-section-${e.detail.sectionId}`) this.open('loading');
        }, opts);
        document.addEventListener('shopify:section:deselect', (e) => {
          if (this.closest('.shopify-section')?.id === `shopify-section-${e.detail.sectionId}`) this.close();
        }, opts);
      }
    }

    disconnectedCallback() {
      this.ac?.abort();
      clearTimeout(this.loadTimer);
      this.game?.pause();
    }

    isNavigation(a) {
      if (!a || a.hasAttribute('download')) return false;
      if (a.target && a.target !== '_self') return false;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return false;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return false; // anclas en la misma página
      return url;
    }

    onLinkClick(e) {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const url = this.isNavigation(e.target.closest?.('a[href]'));
      if (!url) return;
      if (!navigator.onLine) {
        e.preventDefault();
        this.pendingUrl = url.href;
        this.open('offline');
        return;
      }
      // Esperamos un instante: si otro script maneja el clic (carrito, menú), no hacemos nada
      setTimeout(() => { if (!e.defaultPrevented) this.armLoading(); }, 0);
    }

    onSubmit(e) {
      if (!navigator.onLine) {
        e.preventDefault();
        this.open('offline');
        return;
      }
      setTimeout(() => { if (!e.defaultPrevented) this.armLoading(); }, 0);
    }

    armLoading() {
      clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => this.open('loading'), this.delay);
    }

    open(mode) {
      this.mode = mode;
      const offline = mode === 'offline';
      this.titleEl.textContent = offline ? this.dataset.offlineTitle : this.dataset.loadingTitle;
      this.titleEl.classList.toggle('bi-game__dots', !offline);
      this.subEl.textContent = offline ? this.dataset.offlineText : this.dataset.loadingText;
      this.onlineEl.hidden = true;
      if (this.game.state === 'over') {
        this.layerOver.hidden = true;
        this.layerStart.hidden = false;
        this.game.state = 'ready';
        this.game.reset();
        this.game.draw();
      }
      this.game.resize();
      this.game.draw();
      this.classList.add('is-open');
      document.documentElement.classList.add('bi-game-lock');
      this.game.resume();
      this.querySelector('[data-game-play]')?.focus({ preventScroll: true });
    }

    close() {
      clearTimeout(this.loadTimer);
      this.classList.remove('is-open');
      document.documentElement.classList.remove('bi-game-lock');
      this.game.pause();
    }
  }

  customElements.define('bi-delivery-game', BiDeliveryGame);
})();
